








































































































































import Vue from "vue";
import PPOrderItem from "@/picking-portal/components/PPOrderItem.vue";
import PPOrderItemsTable from "@/picking-portal/components/PPOrderItemsTable.vue";
import { FilterDone } from "@/picking-portal/pages/FilterDone";
import { PPCollectItem, PPOrder } from "@/picking-portal/models/PPOrder";
import { orderService } from "@/picking-portal/services/PPOrderService";
import PPBarcodeScanner from "@/picking-portal/components/PPBarcodeScanner.vue";
import PPOrderStatusChip from "@/picking-portal/components/PPOrderStatusChip.vue";
import PPCustomerInfoComponent from "@/picking-portal/components/PPCustomerInfoComponent.vue";
import { printState } from "@/common/state/PrintState";
import { ElementColors } from "@/common/theme/ElementColors";
import { RouteInfoMap, Paths } from "@/common/enums/RouterPaths";
import { storeService } from "@/common/services/StoreService";
import { DateTime } from "luxon";

interface FilterOptions {
  done: FilterDone;
}

interface SortOption {
  title: string;
  compareFn: (a: PPCollectItem, b: PPCollectItem) => number;
}

export default Vue.extend({
  name: "PPPickOrder",
  components: {
    PPOrderStatusChip,
    PPCustomerInfoComponent,
    PPBarcodeScanner,
    PPOrderItem,
    PPOrderItemsTable,
  },
  props: {
    transactionId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      ElementColors,
      isLoading: true,
      FilterDone,
      search: false as boolean,
      order: "" as string,
      items: [] as PPCollectItem[],
      scanning: false as boolean,
      show: false as boolean,
      orderObject: {} as PPOrder,
      barcodesLoading: [] as string[],
      filter: { done: FilterDone.REMAINING } as FilterOptions,
      orderFetched: false as boolean,
      isCustomerDialogOpen: false as boolean,
      sortOptions: [
        {
          title: this.$t("modules.packing-portal.pages.pick-order.sort-by-location"),
          compareFn: (a: PPCollectItem, b: PPCollectItem): number =>
            a.productLocation.localeCompare(b.productLocation, undefined, { numeric: true, sensitivity: "base" }),
        },
        {
          title: this.$t("modules.packing-portal.pages.pick-order.sort-by-description"),
          compareFn: (a, b): number =>
            a.description.localeCompare(b.description, undefined, { numeric: true, sensitivity: "base" }),
        },
        {
          title: this.$t("modules.packing-portal.pages.pick-order.sort-by-quantity-lowest"),
          compareFn: (a: PPCollectItem, b: PPCollectItem): number => a.expectedQuantity - b.expectedQuantity,
        },
        {
          title: this.$t("modules.packing-portal.pages.pick-order.sort-by-quantity-highest"),
          compareFn: (a: PPCollectItem, b: PPCollectItem): number => b.expectedQuantity - a.expectedQuantity,
        },
        {
          title: this.$t("modules.packing-portal.pages.pick-order.sort-by-price-lowest"),
          compareFn: (a: PPCollectItem, b: PPCollectItem): number => a.unitPrice - b.unitPrice,
        },
        {
          title: this.$t("modules.packing-portal.pages.pick-order.sort-by-price-highest"),
          compareFn: (a: PPCollectItem, b: PPCollectItem): number => b.unitPrice - a.unitPrice,
        },
      ] as SortOption[],
      sortOption: (a: PPCollectItem, b: PPCollectItem): number =>
        a.productLocation.localeCompare(b.productLocation, undefined, { numeric: true, sensitivity: "base" }),
      loadingComplete: false,
      storeService,
    };
  },

  computed: {
    breadcrumbs(): Array<Record<string, string | boolean>> {
      return this.$route?.meta?.breadCrumb({
        storeId: storeService.selectedStore,
        date: DateTime.now().toSQLDate(),
        from: this.orderObject?.booking?.start,
        to: this.orderObject?.booking?.end,
      });
    },
    sortedItems: function (): PPCollectItem[] {
      return [...this.items]
        .filter((item) => {
          if (this.filter.done === FilterDone.ALL) {
            return true;
          } else if (this.filter.done === FilterDone.REMAINING) {
            return !item.done;
          } else if (this.filter.done === FilterDone.DONE) {
            return item.done;
          }
          return true;
        })
        .sort(this.sortOption);
    },
    allItemsDone(): boolean {
      return this.items.length > 0 && this.items.every((item) => item.done);
    },
    isOrderFetched(): boolean {
      return this.orderFetched;
    },
    currentStore: function (): string {
      return this.storeService.selectedStore;
    },
  },
  watch: {
    currentStore: function (): void {
      this.changeStore();
    },
  },
  async mounted() {
    await this.getOrder();
    this.orderFetched = true;
    this.isLoading = false;
  },

  methods: {
    getListHeight(): string {
      const toolbarHeight = document.querySelector("header.v-toolbar")?.clientHeight;
      const height = screen.height - (toolbarHeight ? toolbarHeight : 0);
      return `${height}px`;
    },
    getScannerHeight(): number {
      const scannerHeight = document.querySelector("#barcodeScanner")?.clientHeight;
      return scannerHeight ? scannerHeight : 0;
    },
    async getOrder(): Promise<void> {
      this.orderObject = await orderService.getFulfillment(this.transactionId);
      this.items = (this.orderObject.pickingItems ?? []).sort((a, b) => a.description.localeCompare(b.description));
    },
    onOrder(order: string): void {
      this.order = order;
      this.show = true;
    },
    async itemDone(item: PPCollectItem): Promise<void> {
      this.barcodesLoading.push(item.barcode);
      try {
        const indexOfItem = this.items.findIndex((it) => it.barcode === item.barcode);
        const ppUpdatedOrder = await orderService.updateFulfillmentItem(this.transactionId, {
          ...item,
          done: !(item.done ?? false),
        });
        const updatedItems = ppUpdatedOrder.pickingItems;
        this.items = updatedItems;
      } catch (e) {
        console.error(e);
      }
      this.barcodesLoading = this.barcodesLoading.filter((barcode) => barcode !== item.barcode);
    },
    setSortOrder(option: SortOption): void {
      this.sortOption = option.compareFn;
    },
    completeOrder: async function (): Promise<void> {
      this.loadingComplete = true;
      try {
        await orderService.setFulfillmentItemsPicked(this.transactionId, this.items).then(() => {
          this.goToCollectOrder();
        });
      } catch (e) {
        console.error(e);
      }
      this.loadingComplete = false;
    },
    async setAllItemsDone(): Promise<void> {
      this.items = this.items.map((item) => ({ ...item, done: true }));
      await orderService.setFulfillmentItemsPicked(this.transactionId, this.items);
      await this.getOrder();
    },
    openCustomerInfoDialog(): void {
      this.isCustomerDialogOpen = true;
    },
    async onBarcode(barcode: string): Promise<void> {
      const scannedProduct = this.orderObject.pickingItems.find((item) => item.barcode === barcode);
      if (scannedProduct && scannedProduct.collectedQuantity < scannedProduct.expectedQuantity) {
        scannedProduct.collectedQuantity++;
        scannedProduct.done = scannedProduct.collectedQuantity >= scannedProduct.expectedQuantity;
        await orderService.updateFulfillmentItem(this.transactionId, scannedProduct);
        const scannerHeight = this.getScannerHeight();
        await this.$vuetify.goTo("#" + this.getIdForProductElement(barcode), {
          duration: 300,
          offset: scannerHeight,
          container: "#itemCard",
        });
      }
    },
    getIdForProductElement(barcode: string): string {
      return "product_" + barcode;
    },
    print(): void {
      printState.triggerPrint();
    },
    async goToCollectOrder(): Promise<void> {
      const storeId = this.orderObject.storeId.toString();
      const bookingId = this.orderObject.booking.bookingId.toString();
      const transactionId = this.orderObject.transactionId;
      if (!bookingId) return;
      await this.$router.replace({
        name: RouteInfoMap.get(Paths.ClickAndCollectCollectOrder)?.name,
        params: {
          storeId,
          bookingId,
          transactionId,
        },
      });
    },
    async changeStore(): Promise<void> {
      await this.$router
        .push({
          name: RouteInfoMap.get(Paths.ClickAndCollect)?.name,
          query: {
            storeId: storeService.selectedStore.toString(),
            date: DateTime.now().toFormat("yyyy-MM-dd"),
          },
        })
        .catch((error) => {
          if (error.name != "NavigationDuplicated") {
            throw error;
          }
        });
    },
  },
});
